import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';

import { useModal } from 'store/modal';

import { formatDate } from 'utils/helpers/formatters';

import Form from 'components/form/Form';
import FormGroup from 'components/form/FormGroup';
import { IFileDetails, IInitialFileDetails } from 'components/form/types';

interface IProps {
  workTripId: number;
  fromDate?: string;
  toDate?: string;
  task?: string;
  destination?: string;
  onSuccess: (reportDocumentId?: number) => void;
}

interface IFormValues {
  explanation: string;
  difficulties?: string;
  vehicle_registration_number?: string;
  travel_invoice_amount?: number;
  accommodation_invoice_amount?: number;
}

const extractAttachments = (tripData: any): any[] => {
  if (Array.isArray(tripData?.attachments)) return tripData.attachments;
  if (Array.isArray(tripData?.report_documents)) return tripData.report_documents;
  if (Array.isArray(tripData?.report_files)) return tripData.report_files;
  return [];
};

const ReportGenerateFormModal: FC<IProps> = ({
  workTripId,
  fromDate,
  toDate,
  task,
  destination,
  onSuccess,
}: IProps) => {
  const { t } = useTranslation();
  const { showModal, hideModal } = useModal();
  const [uploadedFiles, setUploadedFiles] = useState<IFileDetails[]>([]);
  const [initialUploadedFiles, setInitialUploadedFiles] = useState<IInitialFileDetails[]>([]);
  const [isFileUploading, setIsFileUploading] = useState(false);

  const methods = useForm<IFormValues>({
    defaultValues: {
      explanation: '',
      difficulties: '',
      vehicle_registration_number: '',
      travel_invoice_amount: 0,
      accommodation_invoice_amount: 0,
    },
    shouldFocusError: false,
  });

  const normalizeAttachments = (attachments: any[]): IInitialFileDetails[] =>
    attachments
      .map(item => ({
        id: item?.id,
        name:
          item?.extension && (item?.file_name || item?.name)
            ? `${item?.file_name || item?.name}.${item?.extension}`
            : item?.file_name || item?.name || `#${item?.id}`,
        size: Number(item?.size || 0),
      }))
      .filter(item => item.id);

  useEffect(() => {
    const loadExistingAttachments = async () => {
      try {
        const listRes = await API.get(`work-trips?limit=1&offset=0&id=${workTripId}`);
        const items = listRes.data?.items || listRes.data?.data?.items || [];
        const tripData = items.find((item: any) => Number(item?.id) === Number(workTripId)) || items[0] || null;

        const serverAttachments = extractAttachments(tripData);

        setInitialUploadedFiles(normalizeAttachments(serverAttachments));
      } catch {
        setInitialUploadedFiles([]);
      }
    };

    loadExistingAttachments();
  }, [workTripId]);

  const onFilesUploaded = (files: IFileDetails[]) => {
    setUploadedFiles(current => [...current, ...files]);
    setInitialUploadedFiles(current => [
      ...current,
      ...files
        .filter(file => !!file.id)
        .map(file => ({
          id: file.id,
          name: file.name,
        })),
    ]);
  };

  const onFilesDeleted = (file: File) => {
    setUploadedFiles(current => current.filter(item => file.name !== item.name));
    setInitialUploadedFiles(current => current.filter(item => file.name !== item.name));
  };

  const handleSubmit = async (data: IFormValues) => {
    try {
      const res = await API.post(`work-trips/${workTripId}/report`, {
        explanation: data.explanation,
        description: data.explanation,
        difficulties: data.difficulties || undefined,
        vehicle_registration_number: data.vehicle_registration_number || undefined,
        travel_invoice_amount: Number(data.travel_invoice_amount || 0),
        accommodation_invoice_amount: Number(data.accommodation_invoice_amount || 0),
      });

      hideModal();

      const reportDocumentId = Number(res.data?.report_document_id ?? res.data?.document_id ?? res.data?.id ?? 0);

      onSuccess(reportDocumentId || undefined);

      if (!reportDocumentId) {
        showModal({
          type: 'success',
          content: t('workTrips.reportCreateSuccess'),
        });
      }
    } catch (error) {
      showModal({
        type: 'error',
        content: createErrorFromResponse(error).message,
      });
    }
  };

  return (
    <Form
      methods={methods}
      onSubmit={handleSubmit}
      btnLabel={t('common.saveAndSign')}
      onCancel={hideModal}
      className="px-3 px-md-4"
      isUploading={isFileUploading}
      showGlobalError
    >
      <Row className="mb-2">
        <Col lg={12}>
          <p className="mb-0 text-muted">
            <BoldSubTitle>{t('workTrips.period')}:</BoldSubTitle>{' '}
            {fromDate ? formatDate(new Date(fromDate), 'dd.mm.yyy') : '-'} -{' '}
            {toDate ? formatDate(new Date(toDate), 'dd.mm.yyy') : '-'}
            {' · '}
            <BoldSubTitle>{t('workTrips.task')}:</BoldSubTitle> {task || '-'}
            {' · '}
            <BoldSubTitle>{t('workTrips.destination')}:</BoldSubTitle> {destination || '-'}
          </p>
        </Col>
      </Row>

      <Row>
        <Col
          lg={6}
          className="mb-2"
        >
          <FormGroup
            controlId="formGroupTripReportExplanation"
            label={t('workTrips.reportExplanation')}
            type="text"
            name="explanation"
            as="textarea"
            rows={4}
            isRequired
            maxLength={5000}
            controlGrid={12}
          />
        </Col>
        <Col
          lg={6}
          className="mb-2"
        >
          <FormGroup
            controlId="formGroupTripReportDifficulties"
            label={t('workTrips.reportDifficulties')}
            type="text"
            name="difficulties"
            as="textarea"
            rows={4}
            maxLength={5000}
            controlGrid={12}
          />
        </Col>
      </Row>

      <Row>
        <Col
          lg={6}
          className="mb-2"
        >
          <FormGroup
            controlId="formGroupTripReportVehicleRegistrationNumber"
            label={t('workTrips.reportVehicleRegistrationNumber')}
            type="textarea"
            name="vehicle_registration_number"
          />
        </Col>
        <Col
          lg={6}
          className="mb-2"
        >
          <FormGroup
            controlId="formGroupTripReportTravelInvoiceAmount"
            label={t('workTrips.reportTravelInvoiceAmount')}
            type="number"
            name="travel_invoice_amount"
            isNumber
            min={0}
            controlGrid={12}
          />
        </Col>
        <Col
          lg={6}
          className="mb-2"
        >
          <FormGroup
            controlId="formGroupTripReportAccommodationInvoiceAmount"
            label={t('workTrips.reportAccommodationInvoiceAmount')}
            type="number"
            name="accommodation_invoice_amount"
            isNumber
            min={0}
            controlGrid={12}
          />
        </Col>
      </Row>

      <Row className="file-attachment-container">
        <Col lg={12}>
          <hr />
          <h4 className="mb-2">{t('docWizard.step3.attachDocuments')}</h4>

          <FormGroup
            formGroupClassName="leave-form-dropzone mb-0"
            controlId="formGroupReportFileUpload"
            type="file"
            placeholder={t('fileUploader.placeholder')}
            name="file"
            accept={['application/pdf', '.jpg', '.png', '.heic']}
            multiple
            uploadUri={`work-trips/${workTripId}/upload-attachment`}
            deleteFileUri="work-trips/attachments"
            hint={t('fileUploader.hint', { accept: 'pdf, jpg, png, heic' })}
            onFilesUploaded={onFilesUploaded}
            onFilesDeleted={onFilesDeleted}
            initialUploadedFiles={initialUploadedFiles}
            controlGrid={12}
            isFileUploading={(isUploading: boolean) => {
              setIsFileUploading(isUploading);
            }}
          />

          {uploadedFiles.length > 0 && (
            <p className="mb-0 text-muted">
              {t('common.currentlyUploadedFiles')} {uploadedFiles.length}
            </p>
          )}
        </Col>
      </Row>
    </Form>
  );
};

export default ReportGenerateFormModal;

const BoldSubTitle = styled.span`
  font-weight: bold;
`;
